'use client';

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type AnchorOrigin =
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight';

export interface SnackbarNotification {
    id: string;
    message: string;
    variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
    autoHideDuration?: number;
    action?: ReactNode;
}

export interface SnackbarNotificationContextValue {
    notifications: Record<AnchorOrigin, SnackbarNotification[]>;
    addNotification: (anchor: AnchorOrigin, notification: Omit<SnackbarNotification, 'id'>) => void;
    removeNotification: (anchor: AnchorOrigin, id: string) => void;
    clearAll: (anchor: AnchorOrigin) => void;
}

function createEmptyNotifications(): Record<AnchorOrigin, SnackbarNotification[]> {
    return {
        topLeft: [],
        topCenter: [],
        topRight: [],
        bottomLeft: [],
        bottomCenter: [],
        bottomRight: [],
    };
}

const SnackbarNotificationContext = createContext<SnackbarNotificationContextValue | null>(null);

interface SnackbarNotificationProviderProps {
    maxStack?: number;
    children: ReactNode;
}

export function SnackbarNotificationProvider({
    maxStack = 4,
    children,
}: SnackbarNotificationProviderProps) {
    const [notifications, setNotifications] =
        useState<Record<AnchorOrigin, SnackbarNotification[]>>(createEmptyNotifications);

    const removeNotification = useCallback((anchor: AnchorOrigin, id: string) => {
        setNotifications((prev) => ({
            ...prev,
            [anchor]: prev[anchor].filter((n) => n.id !== id),
        }));
    }, []);

    const addNotification = useCallback(
        (anchor: AnchorOrigin, notification: Omit<SnackbarNotification, 'id'>) => {
            const id = crypto.randomUUID();
            const entry: SnackbarNotification = { ...notification, id };

            setNotifications((prev) => {
                let list = [...prev[anchor], entry];
                if (list.length > maxStack) {
                    list = list.slice(list.length - maxStack);
                }
                return { ...prev, [anchor]: list };
            });

            if (notification.autoHideDuration) {
                setTimeout(() => {
                    removeNotification(anchor, id);
                }, notification.autoHideDuration);
            }
        },
        [maxStack, removeNotification]
    );

    const clearAll = useCallback((anchor: AnchorOrigin) => {
        setNotifications((prev) => ({ ...prev, [anchor]: [] }));
    }, []);

    const value = useMemo<SnackbarNotificationContextValue>(
        () => ({ notifications, addNotification, removeNotification, clearAll }),
        [notifications, addNotification, removeNotification, clearAll]
    );

    return (
        <SnackbarNotificationContext.Provider value={value}>
            {children}
        </SnackbarNotificationContext.Provider>
    );
}

export function useSnackbarNotification(): SnackbarNotificationContextValue {
    const ctx = useContext(SnackbarNotificationContext);
    if (!ctx) {
        throw new Error(
            'useSnackbarNotification must be used within a SnackbarNotificationProvider'
        );
    }
    return ctx;
}
